import React, { useContext } from "react";
import { Box, Grid, Typography } from '@mui/material';
import { Link } from "react-router-dom";
import { LayoutContext } from "../contexts/LayoutContext/LayoutContext";
import { AllAlbumPhotos, EventsList } from "../config";
import { OgMetaTagsPages } from "./ReusableComponents/OgMetaTags";

// Helper function to create low-res versions of images
const createLowResPhotos = (photos, isMobile) => {
    return photos.map(photo => {
        // Option 1: If using a CDN/service that supports URL parameters
        return `${photo}?w=${isMobile ? 50 : 150}&q=30`; // width and quality adjustment

        // Option 2: If using Cloudinary (uncomment if applicable)
        // return photo.replace('/upload/', '/upload/w_150,q_30,f_auto/');

        // Option 3: If you have pre-made thumbnails (uncomment if applicable)
        // return photo.replace('.jpg', '-thumb.jpg');
    });
};

// Create a mosaic container component
const MosaicBackground = ({ images, children, isMobile }) => {
    const lowResImages = createLowResPhotos(images, isMobile);

    return (
        <Box sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
        }}>
            {/* Mosaic grid */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'grid',
                gridTemplateColumns: isMobile
                    ? 'repeat(auto-fill, minmax(50px, 1fr))'
                    : 'repeat(auto-fill, minmax(150px, 1fr))',
                gridAutoRows: isMobile
                    ? 'minmax(50px, auto)'
                    : 'minmax(150px, auto)',
                gap: '1px',
                zIndex: 0,
            }}>
                {lowResImages.map((img, index) => (
                    <Box
                        key={index}
                        component="img"
                        src={img}
                        sx={{
                            width: '100%',
                            height: '100%',
                            aspectRatio: '1/1',
                            objectFit: 'cover',
                            filter: 'brightness(0.85)',
                        }}
                        loading="lazy"
                        decoding="async"
                        alt=""
                    />
                ))}
            </Box>

            {/* Dark overlay */}
            <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
            }}/>

            {/* Content */}
            <Box sx={{ position: 'relative', zIndex: 2 }}>
                {children}
            </Box>
        </Box>
    );
};

export const Events = ({ pageMeta }) => {
    const { isMobile, cardsPadding } = useContext(LayoutContext);

    return (
        <MosaicBackground images={AllAlbumPhotos} isMobile={isMobile}>
            <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{
                    width: '100%',
                    minHeight: '100vh',
                    paddingBottom: isMobile ? 10 : 20,
                    paddingTop: isMobile ? 7 : 17,
                }}
            >
                <OgMetaTagsPages content={pageMeta} />
                <Grid item sm={4} xs={11} sx={{ textAlign: 'center' }} bgcolor={'rgba(255,255,255,0.20)'} borderRadius={1} marginTop={ isMobile ? -10 : 0} marginBottom={ isMobile ? 0 : 15} >
                    <Typography variant="h3" sx={{ color: '#fff' }} bgcolor={'rgba(255,255,255,0.20)'}>
                        Next Events
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#fff' }} bgcolor={'rgba(255,255,255,0.20)'}>
                        Join Camping In The Skies in all their adventures!
                    </Typography>
                </Grid>
                <Grid item
                      container
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      xl={12}
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      gap={2}
                      marginTop={ isMobile ? -20 : -10}
                >
                    {EventsList.map((album, i) => {
                        // Parse the countdown date
                        const countdownDate = new Date(album.eventCountDown).getTime();
                        const currentDate = new Date().getTime();

                        // Add 3 hours (3 * 60 * 60 * 1000 = 10,800,000 ms) to the countdown
                        const hideAfterDate = countdownDate + (3 * 60 * 60 * 1000);
                        const shouldHide = currentDate > hideAfterDate;

                        // Don't render anything if 3 hours have passed since the event finished
                        if (shouldHide) return null;

                        // Otherwise, render the card
                        return album.socialMedia ? (
                            <Grid
                                item key={i}
                                sx={{ padding: cardsPadding, cursor: 'pointer' }}
                                onClick={() => window.open(album.socialMedia, '_blank')}
                            >
                                <Box
                                    sx={{
                                        width: isMobile ? "90vw" : 400,
                                        aspectRatio: '1/1.414',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: '#1f1f1f',
                                        ':hover': {
                                            backgroundColor: '#262626',
                                        },
                                    }}
                                >
                                    <Typography variant="h5" sx={{ color: '#fff', position: 'absolute', backgroundColor: 'rgba(0,0,0,0.7)', padding: '0px 5px', borderRadius: 2 }}>Follow us on Instagram</Typography>
                                    {React.cloneElement(album.icon, { size: 100, firstColor: '#1f1f1f', secondColor: '#ffffff' })}
                                </Box>
                            </Grid>
                        ) : (
                            <Grid item key={i} sx={{ padding: cardsPadding }} bgcolor={'rgba(255,255,255,0.20)'} borderRadius={1}>
                                <Link to={album.url} style={{ textDecoration: 'none' }}>
                                    <Box sx={{
                                        width: isMobile ? "90vw" : 400,
                                        aspectRatio: '1/1.414',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${album.eventCover})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                ':hover': {
                                                    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.0)), url(${album.eventCover})`,
                                                },
                                            }}
                                        />
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                color: '#fff',
                                                textAlign: 'center',
                                                fontSize: 'clamp(0.3rem, 1.2vw, 1rem)',
                                                lineHeight: 1.1,
                                                width: '100%',
                                                padding: '4px',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'normal',
                                                overflow: 'visible',
                                            }}
                                        >
                                            {album.eventName}
                                        </Typography>
                                    </Box>
                                </Link>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>
        </MosaicBackground>
    )
}